<template>
  <v-row class="user-bio-panel">
    <v-col cols="12">
      <v-card class="pt-10">
        <v-card-title
          v-touch="{
            left: () =>
              goToNext(houseData.houseIds, houseData.id, loading, {
                name: 'logistics-apartments-view',
                param: 'id',
              }),
            right: () =>
              goToPrevious(houseData.houseIds, houseData.id, loading, {
                name: 'logistics-apartments-view',
                param: 'id',
              }),
          }"
          class="justify-center flex-column"
        >
          <div class="d-flex justify-space-between align-center w-full mb-2">
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToPrevious(houseData.houseIds, houseData.id) || loading"
              @click="
                goToPrevious(houseData.houseIds, houseData.id, loading, {
                  name: 'logistics-apartments-view',
                  param: 'id',
                })
              "
              ><v-icon>{{ icons.mdiChevronLeft }}</v-icon></v-btn
            >
            <span class="text-center">{{ houseData.name }}</span>
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToNext(houseData.houseIds, houseData.id) || loading"
              @click="
                goToNext(houseData.houseIds, houseData.id, loading, {
                  name: 'logistics-apartments-view',
                  param: 'id',
                })
              "
            >
              <v-icon>{{ icons.mdiChevronRight }}</v-icon>
            </v-btn>
          </div>
          <v-chip
            label
            small
            color="primary"
            class="v-chip-light-bg primary--text font-weight-semibold text-capitalize ma-1 mb-2"
            >{{ statusText }}</v-chip
          >
          <span class="d-flex justify-center align-center flex-wrap">
            <v-chip
              v-for="(tag, index) in houseData.tags"
              :key="index"
              label
              small
              color="info"
              outlined
              class="font-weight-semibold ma-1"
              v-text="tag.tag"
            />
          </span>
        </v-card-title>

        <v-card-text>
          <h2 v-t="'details'" class="text-xl font-weight-semibold mb-2" />

          <v-divider></v-divider>

          <v-list>
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('name') }}:</span>
              <span class="text--secondary">{{ houseData.name }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-1">
              <span>
                <span class="font-weight-medium me-2">{{ $t('description') }}:</span>
                <span class="text--secondary">{{ houseData.description }}</span>
              </span>
            </v-list-item>
          </v-list>

          <h2 v-t="'address'" class="text-xl font-weight-semibold mb-2 mt-4" />

          <v-divider></v-divider>

          <v-list>
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('street') }}:</span>
              <span class="text--secondary">{{ houseData.street }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('houseNumber') }}:</span>
              <span class="text--secondary">{{ houseData.houseNumber }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('flatNumber') }}:</span>
              <span class="text--secondary">{{ houseData.flatNumber }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('city') }}:</span>
              <span class="text--secondary">{{ houseData.city }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('zipCode') }}:</span>
              <span class="text--secondary">{{ houseData.zipCode }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('state') }}:</span>
              <span class="text--secondary">{{ houseData.state }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('country') }}:</span>
              <span class="text--secondary">{{ houseData.country }}</span>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-row justify="center">
            <v-col cols="auto">
              <v-dialog v-model="isDeleteDialogOpen" max-width="650px" persistent>
                <template #activator="{ on, attrs }">
                  <v-btn color="error" v-bind="attrs" v-on="on" @click="isDeleteDialogOpen = true">
                    <v-icon class="me-2">{{ icons.mdiTrashCanOutline }}</v-icon>
                    <span v-t="'delete'" />
                  </v-btn>
                </template>
                <v-card class="pa-sm-10 pa-3">
                  <v-card-title v-t="'delete'" class="justify-center text-h5" />
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" class="d-flex align-center justify-center">{{
                        $t('confirmationModalUndoneQuestion')
                      }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" class="d-flex justify-center mt-3">
                        <v-btn v-t="'discard'" outlined class="me-3" @click="isDeleteDialogOpen = false" />
                        <v-btn v-t="'delete'" color="error" @click="deleteHouseHandler" />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-dialog>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" @click="isBioDialogOpen = !isBioDialogOpen">
                <v-icon class="me-2">{{ icons.mdiPencilOutline }}</v-icon>
                <span v-t="'edit'" />
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <house-bio-edit
        :is-bio-dialog-open.sync="isBioDialogOpen"
        :house-data.sync="houseData"
        :status-choices="statusChoices"
      ></house-bio-edit>
    </v-col>
  </v-row>
</template>

<script>
import { mdiTrashCanOutline, mdiPencilOutline, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { ref, onMounted, computed } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { useRouter } from '@core/utils'
import HouseBioEdit from './HouseBioEdit.vue'
import houseUtils from '@/utils/houseUtils'
import navigationUtils from '@/utils/navigationUtils'

export default {
  components: {
    HouseBioEdit,
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { fetchStatusChoices, statusChoices } = houseUtils()
    const isBioDialogOpen = ref(false)
    const isDeleteDialogOpen = ref(false)
    const { router } = useRouter()

    const { deleteHouse } = useActions('staff', ['deleteHouse'])
    const { houseData } = useState('staff', ['houseData'])

    const { canGoToNext, goToNext, canGoToPrevious, goToPrevious } = navigationUtils()

    const deleteHouseHandler = async () => {
      await deleteHouse(houseData.value.id)
      router.push({ name: 'logistics-apartments-list' })
    }

    const statusText = computed(() => {
      return statusChoices.value.find((status) => status.value === houseData.value.status)?.text
    })

    onMounted(async () => {
      await fetchStatusChoices()
    })

    return {
      canGoToNext,
      goToNext,
      canGoToPrevious,
      goToPrevious,
      deleteHouseHandler,

      statusText,

      houseData,
      statusChoices,
      isBioDialogOpen,
      isDeleteDialogOpen,
      icons: {
        mdiTrashCanOutline,
        mdiPencilOutline,
        mdiChevronLeft,
        mdiChevronRight,
      },
    }
  },
}
</script>
